// Soft edges for a horizontal scroller: the side that still has content past
// it fades out instead of being cut off; at the very start or end that side
// stays crisp. The scrollbar strip at the bottom is left out of the mask.

import { useCallback, useEffect, useState, type CSSProperties } from 'react'

const FADE_PX = 40
const SCROLLBAR_PX = 12

export interface FadeEdges {
  left: boolean
  right: boolean
}

/** Which sides of a horizontal scroller still have content past them (1px tolerance for sub-pixel scroll positions). */
export function fadeEdgesOf(m: { scrollLeft: number; clientWidth: number; scrollWidth: number }): FadeEdges {
  return {
    left: m.scrollLeft > 1,
    right: m.scrollLeft + m.clientWidth < m.scrollWidth - 1,
  }
}

/** The mask for the given edges; undefined (no mask) when both sides are crisp. */
export function edgeFadeStyle(edges: FadeEdges): CSSProperties | undefined {
  if (!edges.left && !edges.right) return undefined
  const left = edges.left ? FADE_PX : 0
  const right = edges.right ? FADE_PX : 0
  const image = `linear-gradient(to right, transparent, #000 ${left}px, #000 calc(100% - ${right}px), transparent), linear-gradient(#000, #000)`
  const size = `100% calc(100% - ${SCROLLBAR_PX}px), 100% ${SCROLLBAR_PX}px`
  return {
    maskImage: image,
    WebkitMaskImage: image,
    maskSize: size,
    WebkitMaskSize: size,
    maskPosition: 'top, bottom',
    WebkitMaskPosition: 'top, bottom',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
  }
}

export function useEdgeFade<T extends HTMLElement>(
  /** Changes when the scroller's content changes (e.g. another stream). */
  contentKey: unknown,
): { ref: (el: T | null) => void; style: CSSProperties | undefined } {
  // A callback ref: the scroller only mounts once the board has loaded.
  const [el, setEl] = useState<T | null>(null)
  const [edges, setEdges] = useState({ left: false, right: false })

  const update = useCallback(() => {
    if (!el) return
    const { left, right } = fadeEdgesOf(el)
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [el])

  useEffect(() => {
    if (!el) return
    update()
    el.addEventListener('scroll', update, { passive: true })
    // The scroller's own size and its columns' sizes both change what is hidden.
    const observer = new ResizeObserver(update)
    observer.observe(el)
    for (const child of Array.from(el.children)) observer.observe(child)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [el, update, contentKey])

  return { ref: setEl, style: edgeFadeStyle(edges) }
}
